import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { SelectionSummary } from '@sorare/core/src/components/layout/SelectionSummary';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';

import { useIsBoostAffordable } from '@sorare/marketplace/src/hooks/useIsBoostAffordable';
import { useLevelUpCards } from '@sorare/marketplace/src/hooks/useLevelUpCards';

import type {
  CardsBoostSummaryQuery,
  CardsBoostSummaryQueryVariables,
} from './__generated__/index.graphql';

type Props = {
  rarity: Rarity;
  onCancel: () => void;
  onBoosted: (cardSlugs: string[]) => void;
  selectedCardsSlugs: Nullable<string[]>;
};

const CARDS_BOOST_SUMMARY_QUERY = gql`
  query CardsBoostSummaryQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      rarity: rarityTyped
      ...useIsBoostAffordable_anyCard
    }
  }
  ${useIsBoostAffordable.fragments.anyCard}
` as TypedDocumentNode<CardsBoostSummaryQuery, CardsBoostSummaryQueryVariables>;

export const CardsBoostSummary = ({
  rarity,
  onCancel,
  onBoosted,
  selectedCardsSlugs,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const track = useEvents();
  const isBoostAffordable = useIsBoostAffordable();
  const { data, loading } = useQuery(CARDS_BOOST_SUMMARY_QUERY, {
    variables: {
      slugs: selectedCardsSlugs || [],
    },
    skip: !selectedCardsSlugs?.length,
  });

  const { levelUpCards, loading: mutationLoading } = useLevelUpCards();

  const rarityXpBalance = currentUser?.xpBalances?.find(
    ({ currency }) => inGameCurrencyToRarity[currency] === rarity
  );

  const selectedCards =
    data?.anyCards?.filter(card => selectedCardsSlugs?.includes(card.slug)) ||
    [];
  const totalXpNeeded = selectedCards.reduce((acc, card) => {
    const { xpNeeded } = isBoostAffordable(card);
    return acc + xpNeeded;
  }, 0);
  const [displayedTotal, setDisplayedTotal] = useState(totalXpNeeded);

  if (displayedTotal !== totalXpNeeded && !loading) {
    setDisplayedTotal(totalXpNeeded);
  }

  return (
    <SelectionSummary
      nbCardsSelected={selectedCardsSlugs?.length || 0}
      color="var(--c-rewards-blue)"
      onCancel={onCancel}
      button={
        <LoadingButton
          loading={mutationLoading}
          startIcon={<Boost size={2} />}
          size="medium"
          color="secondary"
          onClick={async () => {
            const result = await levelUpCards({
              levelUpCards: selectedCards.map(card => ({
                cardSlug: card.slug,
                xpNeeded: (card.xpNeededForNextGrade || 0) - card.xp,
              })),
            });
            const { cards = [] } =
              result.data?.levelUpCardsWithXpCurrency || {};
            if (cards?.length) {
              const cardSlugs = cards.map(card => card.slug);
              track('Click Level Up Cards', {
                cardSlugs,
                rarity,
              });
              onBoosted?.(cardSlugs);
            }
          }}
          disabled={
            !selectedCardsSlugs?.length ||
            loading ||
            (rarityXpBalance?.amount || 0) < totalXpNeeded
          }
        >
          <FormattedMessage
            id="CardsBoostSummary.levelUp"
            defaultMessage="Level Up"
          />
        </LoadingButton>
      }
    >
      <Horizontal>
        <Horizontal gap={0.5}>
          <ScarcityIcon rarity={rarity} />
          <LabelL bold>
            <FormattedNumber value={(selectedCardsSlugs || []).length} />
          </LabelL>
        </Horizontal>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="4"
          height="4"
          viewBox="0 0 4 4"
          fill="none"
        >
          <circle cx="2" cy="2" r="2" fill="white" fillOpacity="0.17" />
        </svg>
        <Horizontal gap={0.5}>
          <Boost rarity={rarity} size={2} />
          <LabelL bold>
            <FormattedNumber value={rarityXpBalance?.amount || 0} />
          </LabelL>
          {!!displayedTotal && (
            <LabelL color="var(--c-red-400)">
              (-
              <AnimatedNumber value={displayedTotal} />)
            </LabelL>
          )}
        </Horizontal>
      </Horizontal>
    </SelectionSummary>
  );
};
